<template>
  <div class="banner">
    <div class="banner-container py-8">
      <!-- Main Content -->
      <div class="content-wrapper">
        <!-- Main Banner Content -->
        <div class="main-content">
          <!-- Navigation Indicator -->
          <div>
            <NavIndicator
              :navIndicator="navIndicator"
              v-if="title"
              class="nav-indicator"
            />
          </div>
          <div>
            <span v-for="tag in tags" :key="tag" class="tag">{{ tag }}</span>
            <h1 class="title">{{ title }}</h1>
            <div class="summary">
              <span class="d-flex ga-1 align-center">
                <p style="font-weight: bold">Date:</p>
                <p>{{ dayjs(parseInt(publishedDate)).format('MMM D, YYYY') }}</p>
              </span>
              <span class="d-flex ga-1 align-center">
                <p style="font-weight: bold">Duration:</p>
                <p>{{ duration }}</p>
              </span>
              <span class="d-flex ga-1 align-center">
                <p style="font-weight: bold">Language:</p>
                <p>{{ language }}</p>
              </span>
            </div>
            <div class="image-container">
              <v-row>
                <v-col cols="12" md="8" align="center">
                  <YoutubePlayer v-if="videoUrl" :videoLink="videoUrl" height="450px" />
                  <img
                    v-else
                    :src="featuredImage"
                    :alt="featuredImageAltText || title"
                    class="featured-image"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                  class="d-flex flex-column"
                  align="center"
                >
                  <div class="download-form-section">
                    <div v-if="!clickedWatchNow">
                      <div class="disclaimer">
                        To access this content, please share your name and email. Your information will only be used by DIGU and will never be shared with third parties.
                      </div>
                      <v-row dense>
                        <v-col cols="12" sm="12">
                          <v-text-field
                            v-model="firstName"
                            label="First Name"
                            variant="outlined"
                            clearable
                            class="input-field"
                            
                            
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="12">
                          <v-text-field
                            v-model="lastName"
                            label="Last Name"
                            variant="outlined"
                            clearable
                            class="input-field"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="12">
                          <v-text-field
                            v-model="email"
                            label="Email"
                            variant="outlined"
                            clearable
                            class="input-field"
                            :rules="[
                              (v) =>
                                /.+@.+\..+/.test(v) || 'Email must be valid',
                            ]"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" class="text-left">
                          <v-btn
                            class="download-button"
                            :loading="loading"
                            @click="watchVideo"
                          >
                            Watch Now
                          </v-btn>
                        </v-col>
                        <v-col
                          cols="12"
                          v-if="errorMessage"
                          class="text-left error"
                        >
                          {{ errorMessage }}
                        </v-col>
                      </v-row>
                    </div>
                    <div v-else class="success-message">
                      <div class="image">
                        <img
                          :src="require('@/assets/img/thankyou-image.svg')"
                          class="thankyou-image"
                        />
                      </div>
                      <p>
                        Thank you
                        <span style="color: #ff6860b2">{{ firstName }}</span>
                        for your interest! Your access has been granted — you may now view the session.
                      </p>
                    </div>
                  </div>
                  <div class="social-share-column2">
                    <SocialShare
                      :title="title"
                      :id="id"
                      :metaDescription="metaDescription"
                      :tags="tags"
                      align="horizontal"
                    />
                  </div>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import NavIndicator from '@/components/partials/NavIndicator.vue';
import SocialShare from './../partials/SocialShare.vue';
import { defineProps, computed, ref, onMounted } from 'vue';
import dayjs from 'dayjs';
import learninghubService from '@/services/API/learninghubService';
import YoutubePlayer from './YoutubePlayer.vue';

const props = defineProps({
  featuredImage: {
    type: String,
    required: true,
  },
  featuredImageAltText: {
    type: String,
    required: false,
  },
  title: {
    type: String,
    required: true,
  },
  tags: {
    type: Array,
    required: false,
  },
  publishedDate: {
    type: String,
    required: true,
  },
  duration: {
    type: String,
    required: true,
  },
  language: {
    type: String,
    required: true,
  },
  slug: {
    type: String,
    required: false,
  },
  id: {
    type: String,
    required: false,
  },
  metaDescription: {
    type: String,
    required: false,
  },
});

const navIndicator = computed(() => [
  { text: 'Home', icon: 'fas fa-home', link: '/' },
  { text: 'Learning Hub', link: '/learning-hub' },
  { text: props.title || '', link: '' },
]);
const firstName = ref('');
const lastName = ref('');
const email = ref('');
const loading = ref(false);
const clickedWatchNow = ref(false);
const errorMessage = ref('');
const videoUrl = ref('');

// Load state from localStorage on component mount
onMounted(() => {
  const storedState = localStorage.getItem(`videoAccess_${props.slug}`);
  if (storedState) {
    const { firstName: storedFirstName, email: storedEmail, videoUrl: storedVideoUrl } = JSON.parse(storedState);
    if (storedFirstName && storedEmail && storedVideoUrl) {
      firstName.value = storedFirstName;
      email.value = storedEmail;
      videoUrl.value = storedVideoUrl;
      clickedWatchNow.value = true;
    }
  }
});

const watchVideo = async () => {
  if (!firstName.value?.trim()) {
    errorMessage.value = 'First Name is required';
    return;
  }
  if (!lastName.value?.trim()) {
    errorMessage.value = 'Last Name is required';
    return;
  }
  if (!email.value?.trim() || !/.+@.+\..+/.test(email.value)) {
    errorMessage.value = 'A valid Email is required';
    return;
  }
  errorMessage.value = '';

  loading.value = true;
  try {
    // Fetch video link
    const videoResponse = await learninghubService.getLearninghubVideoLink(
      props.slug,
      {
        email: email.value,
        firstName: firstName.value,
        lastName: lastName.value,
      }
    );

    if (videoResponse.status === 200 && videoResponse.data) {
      videoUrl.value = videoResponse.data; // Set video URL to display video player
      clickedWatchNow.value = true; // Hide form after successful registration

      // Save state to localStorage
      localStorage.setItem(
        `videoAccess_${props.slug}`,
        JSON.stringify({
          firstName: firstName.value,
          email: email.value,
          videoUrl: videoUrl.value,
        })
      );
    } else {
      errorMessage.value = 'Failed to fetch video link';
    }
    console.log('Video Link: ', videoUrl.value);

    // Submit newsletter subscription at the same time
    try {
      await learninghubService.createNewsletter({
        email: email.value,
        firstName: firstName.value,
        lastName: lastName.value,
      });
    } catch (newsletterError) {
      console.warn('Newsletter subscription failed:', newsletterError);
      // Continue even if newsletter subscription fails
    }
  } catch (error) {
    console.error('Error during video fetch:', error);
    errorMessage.value = 'An error occurred. Please try again.';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.banner {
  background-size: cover;
  /* background-position: center; */
  background-repeat: no-repeat;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0 7rem;

}

.banner-container {
  /* padding: 32px; */
  padding-bottom: 0;
  max-width: 1250px;
  /* justify-content: ; */
  align-items: le;

}
.featured-image {
  width: 100%;
  height: auto;
  max-height: 500px;
  object-fit: cover;
  border-radius: 8px;
  aspect-ratio: 1.7777;
}
.nav-indicator {
  margin-bottom: 16px;
  text-align: left;
}

.content-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 32px;
}

.image-container {
  display: flex;
  margin-bottom: 2rem;
}

.download-form-section {
  background-color: white;
  padding: 1rem 1rem;
  margin-left: 1rem;
  width: 100%;
  border-radius: 15px;
}
.social-share-column2{
  margin-left: 1rem;
  width: 90%;
}
.disclaimer {
  border-radius: 10px 10px 1px 1px;
  background-color: #cde9f7;
  padding: 1rem 1rem;
  margin-bottom: 2rem;
  text-align: left;
  font-size: 14px;
  color: #0097dd;
}
.input-field {
  color: #686868 !important;
  
}
.download-button {
  background-color: #007ab2;
  text-transform: capitalize;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease-in-out, transform 0.2s ease-in-out;
  height: 50px;
}
.download-button:hover {
  background-color: #00aeff;
  transform: scale(1.05);
}
.error {
  color: red;
  font-size: 0.9em;
}
.success-message {
  display: grid;
  width: 100%;

  padding-top: 0;
  color: #007ab2;
  font-size: 1em;
  text-align: center;
  align-items: center;
  justify-content: center;
}
.thankyou-image {
  width: 80%;
  max-width: 500px;
  min-width: 300px;
  max-height: 250px;
  margin-bottom: 1rem;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.title {
  font-size: clamp(28px, 5vw, 44px);
  color: #292929;
  margin-bottom: 1rem;
}
.tag {
  display: inline-block;
  background-color: #e0e7ff;
  color: #4b5eaa;
  padding: 4px 16px;
  border-radius: 12px;
  margin-bottom: 1rem;
  font-size: 0.8em;
}
.summary {
  display: flex;
  justify-content: center;
  gap: 32px;
  color: #686868;
  font-size: 14px;
  margin-bottom: 2rem;
  overflow-x: auto;
}

@media (max-width: 468px) {
  .summary {
    display: grid;
    justify-content: left;
    color: #686868;
    font-size: 14px;
    margin-bottom: 3rem;
    overflow-x: auto;
    gap: 5px;
    margin-left: 2px;
  }
  .main-content {
    text-align: left;
  }
}

@media (max-width: 768px) {
  .content-wrapper {
    flex-direction: column;
    align-items: center;
  }
  .image-container {
    margin-bottom: 0px;
  }
  .download-form-section {
   
    margin-left: 0rem;
    
  }
  .social-share-column2{
    /* margin-left: 0rem; */
  }
}

@media (max-width: 760px) {
  .download-form-section {
    padding: 2rem 1rem;
  }
}
@media only screen and (max-width: 1280px){
  .banner {
    padding: 0 6rem;
  }
}
@media only screen and (max-width: 1050px){
  .banner {
    padding: 0 4.5rem;
  }
}
@media only screen and (max-width: 840px){
  .banner {
    padding: 0 4rem;
  }
}
@media only screen and (max-width: 640px){
  .banner {
    padding: 0 3rem;
  }
}
@media only screen and (max-width: 470px){
  .banner {
    padding: 0 2rem;
  }
}
@media only screen and (max-width: 370px){
  .banner {
    padding: 0 2rem;
  }
}
</style>